import * as Api from 'api/analytics';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { getAuth, hasSHAWAnalyticsPermission } from 'actions/auth/login';
import {
  getEndTimeRequest,
  getGroupFilterID,
  getStartTimeRequest
} from './selectors';
import { delay } from 'redux-saga';
import { getLocale } from 'actions/language/selectors';
import moment from 'moment';
import { pushFormattedError } from 'helpers/formValidationHelper/formValidationMessage';
import { ERROR_ACCESS_DENIED } from 'lib/errorTypes';

// Actions
export const FETCH_DATA = 'SHAW_ANALYTICS/FETCH_DATA';
export const SUCCESS_FETCHING_DATA = 'SHAW_ANALYTICS/SUCCESS_FETCHING_DATA';

export const SET_LOADING = 'UI/SHAW_ANALYTICS/SET_LOADING';
export const CHANGE_DATE = 'UI/SHAW_ANALYTICS/CHANGE_DATE';
export const TOGGLE_DATEPICKER_DROPDOWN =
  'UI/SHAW_ANALYTICS/TOGGLE_DATEPICKER_DROPDOWN';
export const SET_GROUP_FILTER = 'UI/SHAW_ANALYTICS/SET_GROUP_FILTER';

/*** Action Creators ***/
export const fetchData = () => ({
  type: FETCH_DATA
});

export const fetchDataSuccess = (
  surveyStatisticsStart,
  surveyStatisticsComplete,
  shawData,
  surveyDataRequestedCallback,
  surveyDataEligibleResponses
) => ({
  type: SUCCESS_FETCHING_DATA,
  surveyStatisticsStart,
  surveyStatisticsComplete,
  shawData,
  surveyDataRequestedCallback,
  surveyDataEligibleResponses
});

export const setIsLoading = () => ({
  type: SET_LOADING
});

export const changeDate = ({ startDate, endDate }) => ({
  type: CHANGE_DATE,
  startDate,
  endDate
});

export const toggleDropdown = () => ({
  type: TOGGLE_DATEPICKER_DROPDOWN
});

export const setGroupFilter = (groupFilter) => ({
  type: SET_GROUP_FILTER,
  groupFilter
});

/*** Reducer ***/
export const DEFAULT_STATE = {
  surveyStatisticsStart: [],
  surveyStatisticsComplete: [],
  shawData: [],
  surveyDataRequestedCallback: [],
  surveyDataEligibleResponses: [],
  datepickerDropdownOpen: false,
  startDate: new moment().subtract(7, 'd'),
  endDate: new moment().subtract(1, 'd'),
  groupFilter: { id: '' },
  isLoading: false
};

export default function SHAWAnalyticsReducer(
  state = DEFAULT_STATE,
  {
    type,
    surveyStatisticsStart,
    surveyStatisticsComplete,
    shawData,
    surveyDataRequestedCallback,
    surveyDataEligibleResponses,
    startDate,
    endDate,
    groupFilter
  } = {}
) {
  const { datepickerDropdownOpen } = state;
  switch (type) {
    case SUCCESS_FETCHING_DATA:
      return {
        ...state,
        surveyStatisticsStart,
        surveyStatisticsComplete,
        shawData,
        surveyDataRequestedCallback,
        surveyDataEligibleResponses,
        isLoading: false
      };
    case SET_LOADING:
      return {
        ...state,
        isLoading: true
      };
    case CHANGE_DATE:
      return {
        ...state,
        startDate,
        endDate
      };
    case TOGGLE_DATEPICKER_DROPDOWN:
      return {
        ...state,
        datepickerDropdownOpen: !datepickerDropdownOpen
      };
    case SET_GROUP_FILTER: {
      return {
        ...state,
        groupFilter
      };
    }
    default:
      return state;
  }
}

/*** Sagas ***/
// Worker
export function* reloadSHAWAnalyticsData() {
  yield put(fetchData());
}

export function* fetchSHAWAnalyticsWorker() {
  try {
    yield call(delay, 0);

    const authorization = yield select(getAuth);
    const locale = yield select(getLocale);
    const startDate = yield select(getStartTimeRequest);
    const endDate = yield select(getEndTimeRequest);
    const userGroupId = yield select(getGroupFilterID);
    const hasPermission = yield select(hasSHAWAnalyticsPermission);

    if (!authorization || !hasPermission) {
      yield pushFormattedError({ id: ERROR_ACCESS_DENIED });
      return;
    }

    yield put(setIsLoading());
    const SHAWAnalytics = yield call(
      Api.getSHAWAnalytics,
      authorization,
      locale,
      startDate,
      endDate,
      userGroupId
    );

    yield put(
      fetchDataSuccess(
        SHAWAnalytics.data.surveyStatisticsStart,
        SHAWAnalytics.data.surveyStatisticsComplete,
        SHAWAnalytics.data.shawData,
        SHAWAnalytics.data.surveyDataRequestedCallback,
        SHAWAnalytics.data.surveyDataEligibleResponses
      )
    );
  } catch (err) {
    if (
      err.errors &&
      err.errors.some((error) => error.message == 'Access denied error.')
    ) {
      // does not have SHAW analytics scope
      yield pushFormattedError({ id: ERROR_ACCESS_DENIED });
    } else {
      yield pushFormattedError(err);
    }
  }
}

// Watcher
export function* fetchSHAWAnalyticsDataSaga() {
  yield all([
    takeLatest(FETCH_DATA, fetchSHAWAnalyticsWorker),
    takeLatest(CHANGE_DATE, reloadSHAWAnalyticsData),
    takeLatest(SET_GROUP_FILTER, reloadSHAWAnalyticsData)
  ]);
}



// WEBPACK FOOTER //
// ./src/actions/SHAWAnalytics/actions.js